import React, { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { usePage, useViewport } from '@/src/app/hooks';
import { ResourceBoxInner, ErrorMessage, Loading } from '@/src/ui';
import type { ResourceHeaderDTO } from './RenderResourceSubmenu';
import './css/ResourceBoxes.css';

export interface UnitDTO {
    id: number;
    unitNumber: number;
    unitTitle: string;
    lessons: LessonDTO[];
}

export interface LessonDTO {
    id: number;
    lessonNumber: number;
    title: string;
    lessonType?: string;
    subitems: LessonResourceDTO[];
}

export interface LessonResourceDTO {
    id: number;
    text: string;
    type: string;
    link: string;
    thumbnail: string;
}

interface ResourceBoxesProps {
    resourcesData: ResourceHeaderDTO[] | null;
    loading: boolean;
    error: string;
}

const ResourceBoxes: React.FC<ResourceBoxesProps> = ({ resourcesData, loading, error }) => {
    const { pageGrade, setPageGrade } = usePage();
    const { isMobile } = useViewport();

    const params = useParams();
    const grade = params.grade || pageGrade;
    if (grade !== pageGrade) {
        setPageGrade(grade);
    }

    const renderColumn = useCallback(
        (colNo: number) => {
            const boxes = resourcesData?.filter((box) => box.columnNumber === colNo) || [];
            return (
                <>
                    {boxes.map((box) => (
                        <div
                            key={`resources-box-c${colNo}-t${box.id}`}
                            className={`resources-box background pd-G${pageGrade}-primary`}
                        >
                            <div className="tile-headings">{box.title}</div>
                            {box.items.map((item) => {
                                const anchorId = `resource-container-c${colNo}-${item.id}`;
                                return (
                                    <ResourceBoxInner
                                        key={anchorId}
                                        id={anchorId}
                                        item={item}
                                        grade={pageGrade}
                                        namespace="resources"
                                    />
                                );
                            })}
                        </div>
                    ))}
                </>
            );
        },
        [resourcesData, pageGrade]
    );

    return (
        <>
            <ErrorMessage error={error} />
            {loading ? (
                <Loading />
            ) : (
                <div id="resources-container" className="container resources-container">
                    <div className={`${isMobile ? 'col' : 'row'}`}>
                        <div className={`resources-column col ${isMobile ? 'mb-4' : ''}`}>
                            {renderColumn(1)}
                        </div>
                        <div className={`resources-column col ${isMobile ? 'mb-4' : ''}`}>
                            {renderColumn(2)}
                        </div>
                        <div className={`resources-column col ${isMobile ? 'mb-4' : ''}`}>
                            {renderColumn(3)}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ResourceBoxes;
